import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import { ArrowRight, BadgeCheck, FileText } from "lucide-react"; // Lucide icons
import { motion } from "framer-motion";

import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI (Return to Invoice)
  FaTruckPickup, // For Roadside Assistance
  FaCogs, // For Consumables Cover
  FaCheckCircle,
  FaRoad,
  FaGavel,
  FaCloudRain,
  FaCarCrash,
} from "react-icons/fa";

import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";
function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const MarutiSuzukiCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const faqs = [
    {
      question: "What is car insurance?",
      answer:
        "Car insurance is a contract in which an insurance company agrees to cover certain costs associated with owning and driving a car. This can include coverage for accidents, theft, damage, and liability for injuries to others. It is designed to protect you from financial losses related to your vehicle.",
    },
    {
      question:
        "What factors should I consider when choosing a car insurance policy?",
      answer:
        "When selecting car insurance, consider factors like the types of coverage (liability, collision, comprehensive, etc.), deductible amounts, policy limits, and premium costs. You should also evaluate whether additional features like roadside assistance or rental car reimbursement are important to you, and check for any available discounts, such as multi-policy or safe driving discounts.",
    },
    {
      question: "Why is car insurance important?",
      answer:
        "Car insurance is important because it provides financial protection in the event of an accident, theft, or other damage to your vehicle. It also offers liability coverage to protect you from legal claims if you are responsible for injuring someone or damaging their property while driving. Without car insurance, you could face substantial financial burdens or legal consequences.",
    },
    {
      question: "Can I buy car insurance through TheFinMaster.com?",
      answer:
        "TheFinMaster.com is not a car insurance provider. We offer educational resources, policy comparison tools, and unbiased information to help you understand various car insurance options and choose the best policy for your needs. Once you have selected the right coverage, you will be directed to the insurance provider’s website to complete a secure application process.",
    },
    {
      question:
        "What is the difference between third-party and comprehensive car insurance?",
      answer:
        "Third-party insurance covers damages to other people or property caused by your car, which is mandatory by law. Comprehensive insurance covers both third-party liabilities and damages to your own vehicle due to accidents, theft, natural calamities, or fire.",
    },
    {
      question: "How is the premium for car insurance calculated?",
      answer:
        "Car insurance premiums are calculated based on factors like the car's make, model, age, location, fuel type, engine capacity, and claim history. Comprehensive policies also consider add-ons and declared insured value (IDV).",
    },
    {
      question: "What is a No Claim Bonus (NCB) in car insurance?",
      answer:
        "NCB is a reward given by insurers for not making any claims during the policy year. It translates into a discount on your renewal premium and increases with each claim-free year, up to a certain limit, making future policies more affordable.",
    },
    {
      question: "Can I transfer my car insurance when I sell my vehicle?",
      answer:
        "Yes, car insurance can be transferred to the new owner along with the vehicle. You need to notify the insurer, provide the sale documents, and request a policy transfer, which may involve an endorsement and change in ownership details.",
    },
    {
      question: "What is an IDV in car insurance?",
      answer:
        "IDV (Insured Declared Value) is the maximum amount the insurer will pay if your car is stolen or declared a total loss. It is based on the car’s current market value and decreases with depreciation each year, impacting your premium and claim amount.",
    },
    {
      question: "What documents are required to buy or renew car insurance?",
      answer:
        "To buy or renew car insurance, you typically need your car's registration certificate (RC), previous insurance details (if applicable), valid ID proof, and address proof. Some insurers may also request inspection in case of policy break.",
    },
    {
      question: "Are add-ons in car insurance worth it?",
      answer:
        "Add-ons like zero depreciation cover, roadside assistance, engine protection, and return to invoice can enhance your coverage. While they increase the premium, they offer additional peace of mind, especially for new or high-end cars.",
    },
    {
      question: "What is zero depreciation cover in car insurance?",
      answer:
        "Zero depreciation, or bumper-to-bumper cover, ensures you receive the full claim amount without any deduction for depreciation on car parts. It is ideal for new cars and helps reduce out-of-pocket expenses during repairs after an accident.",
    },
    {
      question: "Can I cancel my car insurance policy mid-term?",
      answer:
        "Yes, you can cancel your car insurance before its expiry. You must provide a valid reason and submit the required documents, like proof of new insurance or RC cancellation. Refunds are processed based on the unused policy period.",
    },
    {
      question: "How do I file a claim for car insurance?",
      answer:
        "To file a claim, contact your insurer immediately and provide details of the incident. Submit the required documents such as FIR (if needed), RC, driving license, and photos of the damage. The insurer will inspect and process your claim accordingly.",
    },
    {
      question: "Which Maruti car has the lowest insurance premium?",
      answer:
        "The Alto K10 generally has the lowest premium due to its price and engine size.",
    },
    {
      question: "Is zero depreciation cover important for Swift or Baleno?",
      answer:
        "Yes, it’s highly recommended for cars under 5 years old or if you drive regularly.",
    },
    {
      question: "Can I carry forward my NCB to a new Maruti car?",
      answer:
        "Absolutely. Your No Claim Bonus can be transferred to a new vehicle under your name.",
    },
    {
      question: "What’s the average insurance cost for Brezza?",
      answer:
        "Usually ranges from ₹3,500 to ₹6,000 depending on variant and city.",
    },
    {
      question: "Should I get RTI cover for a new Dzire?",
      answer:
        "Yes, especially during the first few years of ownership, RTI offers added financial protection.",
    },
    {
      question: "Is Maruti’s in-house insurance better than others?",
      answer:
        "It depends. Maruti Insurance simplifies paperwork, but you should always compare third-party options for better pricing or add-ons.",
    },
    {
      question: "Do I need engine protection for a petrol Maruti?",
      answer:
        "It’s optional but helpful in flood-prone areas or cities with poor drainage.",
    },
    {
      question: "Can I renew my policy online if it’s expired?",
      answer: "Yes, though your car may need a physical inspection first.",
    },
    {
      question: "What’s the benefit of choosing a cashless garage?",
      answer:
        "Repairs are done without you having to pay upfront — your insurer settles the bill directly.",
    },
    {
      question: "Does the city I live in affect my insurance premium?",
      answer:
        "Yes, premiums tend to be higher in metro cities due to higher traffic and accident risk.",
    },
    {
      question: "Is it necessary to inform my insurer before installing CNG?",
      answer:
        "Yes, any modification like a CNG kit must be declared, or claims could be denied.",
    },
    {
      question: "How can I check if my policy is active?",
      answer:
        "You can check through the insurer’s website or the official VAHAN portal using your registration number.",
    },
    {
      question: "What’s covered under comprehensive insurance?",
      answer:
        "It includes third-party liability plus own damage, fire, theft, and natural disasters.",
    },
    {
      question: "Can I cancel my insurance mid-term?",
      answer:
        "Yes, provided you have a valid reason and a replacement policy (if required by law).",
    },
    {
      question: "Do Maruti EVs have different insurance rates?",
      answer:
        "Insurance for electric variants like the eVX (upcoming) may differ slightly due to component costs.",
    },
    {
      question: "Can I add family members as named drivers?",
      answer: "Yes, if your insurer allows multiple driver coverage.",
    },
    {
      question: "What’s the penalty for driving without insurance in India?",
      answer:
        "₹2,000 fine for the first offence and ₹4,000 for repeat violations.",
    },
    {
      question: "Are accessories like alloy wheels insured?",
      answer:
        "Only if declared during policy purchase and included in the insured value.",
    },
    {
      question: "Do I get a discount if I install a dash cam?",
      answer:
        "Currently, insurers don’t offer discounts for dash cams, but it can help during claim disputes.",
    },
    {
      question: "What if I sell my car mid-policy?",
      answer:
        "The policy can be transferred to the new owner, or you can cancel and claim a refund for the unused premium.",
    },
  ];

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Covers the full cost of replaced parts without factoring in depreciation.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection",
      description:
        "Useful in flood-prone cities or for frequent highway users.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description: "Helps recover full invoice value in case of total loss.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Supports you during breakdowns, flat tyres, or towing needs.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        " Includes small parts like nuts, oils, and brake fluids that are often excluded.",
      icon: <FaCogs className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: " Easy to Use on Any Device" },
    { icon: "💰 ", text: " Helps You Save on Insurance" },
  ];
  const marutiinsurance = [
    {
      name: 'Alto K10',
      path: '/maruti-suzuki-alto-car-insurance',
      image: '/assets/Altok10car.jpeg',
    },
      {
      name: 'Wagon R',
      path: '/maruti-suzuki-wagonr-car-insurance',
      image: '/assets/wagonrcar.jpeg',
    },
       {
      name: 'Swift',
      path: '/maruti-suzuki-swift-car-insurance',
      image: '/assets/swiftcar.jpeg',
    },
      {
      name: 'Baleno',
      path: '/maruti-suzuki-baleno-car-insurance',
      image: '/assets/balenocar.jpeg',
    },
     {
      name: 'Dzire',
      path: '/maruti-suzuki-dzire-car-insurance',
      image: '/assets/dzirecar.jpeg',
    },
    {
      name: 'Ertiga',
      path: '/maruti-suzuki-ertiga-car-insurance',
      image: '/assets/ertigacar.jpeg',
    },
    {
      name: 'Brezza',
      path: '/maruti-suzuki-brezza-car-insurance',
      image: '/assets/brezzacar.jpeg',
    },
     {
      name: 'Fronx',
      path: '/maruti-suzuki-fronx-car-insurance',
      image: '/assets/fronxcar.jpeg',
    },
    
    
   
  ];
  return (
    <>
      <Helmet>
        <title>
          Compare Maruti Suzuki Car Insurance in India - Compare Quotes & Buy
          Online
        </title>
        <meta
          name="description"
          content="Explore Maruti Suzuki car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Maruti  vehicle"
        />
        <meta
          name="keywords"
          content="maruti car insurance, auto insurance india, maruti suzuki  insurance, car insurance quotes"
        />
        <link rel="canonical" href="https://thefinmaster.com/maruti-suzuki-car-insurance" />
       <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      {/* Your existing content */}
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>

          <li className="flex items-center">
            <span className="text-gray-500 ">Maruti Suzuki Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600 ">
                    Maruti Suzuki Car Insurance{" "}
                  </span>{" "}
                 
                </h1>
                <p className="text-lg md:text-base lg:text-lg  mt-4 pl-4">
                Maruti Suzuki is a brand that's synonymous with Indian roads. Whether you own a compact Alto or a feature-rich Grand Vitara, it's important to keep your car protected, and that starts with having the right insurance. This guide walks you through everything you need to know about insuring a Maruti Suzuki car, from average premiums and model insights to helpful add-ons, renewal tips, and FAQs.{" "}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="/assets/marutiinsurance.jpeg"
                alt="Noimage"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center  p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center ">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Maruti Suzuki
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Maruti Suzuki has long been India’s top-selling car manufacturer,
            trusted for its affordability, low maintenance costs, and widespread
            service support. With a diverse range of models - hatchbacks,
            sedans, SUVs, and MPVs, it caters to everyone from first-time buyers
            to family car owners.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            If you own a Maruti, it’s essential to pair it with a well-suited
            car insurance policy that offers both value and peace of mind.
          </p>
        </div>
      </div>
   <div className="p-4 sm:p-8 bg-white">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
        Choose Your Car Brand
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
  {marutiinsurance.map((calc, index) => (
    <motion.div
      key={index}
      whileHover={{ scale: 1.04 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Link
        to={calc.path}
        className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
      >
        <img
          src={calc.image}
          alt={calc.name}
          className="w-16 h-16 object-contain mb-2"
        />
        <span className="text-md font-medium text-gray-800 break-words leading-snug">
          {calc.name}
        </span>
      </Link>
    </motion.div>
  ))}
</div>
</div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>

      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        {/* Soft glowing background */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Maruti Car Insurance Matters
          </h2>

          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            While Maruti cars are known for being reliable and budget-friendly,
            accidents, theft, and natural disasters can happen anytime. Car
            insurance protects you from unexpected repair costs and legal
            liabilities.
          </p>

          {/* Feature List */}
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Damage due to accidents or fire</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-yellow-500 mt-1" />
              <span>Theft or vandalism</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudRain className="text-xl text-blue-500 mt-1" />
              <span>Natural calamities (floods, storms, etc.)</span>
            </li>
            <li className="flex items-start gap-3">
              <FaGavel className="text-xl text-green-500 mt-1" />
              <span>Third-party legal liability</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Cashless repairs at network garages</span>
            </li>
          </ul>

          {/* Ending Paragraph Highlight */}
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Having the right insurance can also help retain your car’s resale
            value and reduce financial stress during emergencies.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          {/* Left - Text and CTA */}
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Right - Feature List with Emojis */}
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          What’s Protected Under Your Car Insurance?
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here are some of Maruti Suzuki’s top-selling cars along with typical
          insurance ranges (based on standard plans and average IDV):
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Mobile View: Stacked Cards */}
          <div className="block md:hidden">
            {[
              {
                model: "Alto K10",
                category: "Hatchback",
                idv: "3.5–4.5 Lakh",
                premium: "₹2,100 onwards",
              },
              {
                model: "Wagon R",
                category: "Hatchback",
                idv: "4.5–6.5 Lakh",
                premium: "₹2,400 – ₹3,800",
              },
              {
                model: "Swift",
                category: "Hatchback",
                idv: "6–8 Lakh",
                premium: "₹2,800 – ₹4,500",
              },
              {
                model: "Baleno",
                category: "Premium Hatch",
                idv: "7–9 Lakh",
                premium: "₹3,000 – ₹5,000",
              },
              {
                model: "Dzire",
                category: "Sedan",
                idv: "7–9 Lakh",
                premium: "₹2,900 – ₹4,500",
              },
              {
                model: "Brezza",
                category: "Compact SUV",
                idv: "9–12 Lakh",
                premium: "₹3,500 – ₹6,000",
              },
              {
                model: "Grand Vitara",
                category: "SUV",
                idv: "11–18 Lakh",
                premium: "₹4,200 – ₹7,500",
              },
              {
                model: "Ertiga",
                category: "MPV",
                idv: "9–12 Lakh",
                premium: "₹3,700 – ₹6,500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View: Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Alto K10", "Hatchback", "3.5–4.5 Lakh", "₹2,100 onwards"],
                  ["Wagon R", "Hatchback", "4.5–6.5 Lakh", "₹2,400 – ₹3,800"],
                  ["Swift", "Hatchback", "6–8 Lakh", "₹2,800 – ₹4,500"],
                  ["Baleno", "Premium Hatch", "7–9 Lakh", "₹3,000 – ₹5,000"],
                  ["Dzire", "Sedan", "7–9 Lakh", "₹2,900 – ₹4,500"],
                  ["Brezza", "Compact SUV", "9–12 Lakh", "₹3,500 – ₹6,000"],
                  ["Grand Vitara", "SUV", "11–18 Lakh", "₹4,200 – ₹7,500"],
                  ["Ertiga", "MPV", "9–12 Lakh", "₹3,700 – ₹6,500"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Smart Add-Ons for Maruti Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Depending on your car model and driving habits, these optional covers
          can be worth considering:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className=" mx-auto container p-6 mt-6 ">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers Trusted by Maruti Owners
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          These insurance companies offer policies that match the needs of
          Maruti vehicle users:
        </p>

        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl"> 🏥 </span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers a wide range of add-ons and has a large service network.
            </p>
          </div>

          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl"> 📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Provides flexible plans with excellent coverage options.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Ideal for both personal and commercial Maruti models.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for quick claim processing and digital policy options.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl"> 🛠️</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers efficient customer support and wide cashless garage access.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl"> 🧾</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Add-Ons Not Purchased
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              If you haven’t opted for specific add-ons like zero depreciation
              or roadside assistance, those benefits won’t be covered.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on Premiums
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold  text-gray-800 dark:text-white mb-6">
              Here are a few simple ways to reduce your car insurance cost
              without compromising on coverage
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Identity Proof
                </span> */}
                <span>
                  {" "}
                  Renew your policy on time to retain No Claim Bonus (NCB).
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Address Proof
                </span> */}
                <span>
                  {" "}
                  Compare multiple plans online to find better rates.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Income Proof
                </span> */}
                <span>
                  {" "}
                  Opt for voluntary deductibles if you’re a confident driver.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Photograph
                </span> */}
                <span>Install certified anti-theft devices.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Photograph
                </span> */}
                <span>
                  {" "}
                  Avoid making small claims to preserve your NCB slab.
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Insurance/Renewal
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold  text-gray-800 dark:text-white mb-6">
              To purchase or renew car insurance, you’ll typically need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Minimum Age
                </span> */}
                <span> Vehicle Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Maximum Age
                </span> */}
                <span> Previous policy details (if renewing)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Income Criteria
                </span> */}
                <span> PAN or Aadhaar. </span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Credit Score
                </span> */}
                <span> Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Employment Status
                </span> */}
                <span> Photos of the car (for expired policies or claims)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>

        {/* Main Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Icon */}
            <div className="text-indigo-600 text-5xl">
              <FaRoad />
            </div>

            {/* Text Content - REMAINS UNCHANGED */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Insuring your Maruti Suzuki car is more than just a formality.
                It’s about long-term protection, convenience, and peace of mind.
                Whether you drive a Swift, Dzire, or Grand Vitara, choosing a
                well-matched insurance plan ensures you’re always ready for the
                road ahead.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare policies, understand what
                you’re buying, and make confident decisions, so you save time,
                money, and stress.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default MarutiSuzukiCarInsurance;
